import { useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  MapPin,
  Package,
  QrCode,
  RotateCcw,
  Trash2,
  Truck,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import { Progress } from "~/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { useToast } from "~/hooks/use-toast";
import {
  useCheckAndCompleteReturnRequest,
  useReturnRequest,
  useReturnRequestItems,
  useRouteInspection,
  useUpdateReturnRequestStatus,
} from "~/hooks/wms";
import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";
import type { DispositionType, ReturnInspection } from "~/types/returns";
import {
  DISCARD_REASON_LABELS,
  DISPOSITION_COLORS,
  DISPOSITION_LABELS,
} from "~/types/returns";

const dispositionIcons: Record<DispositionType, React.ElementType> = {
  SELLABLE: RotateCcw,
  REPACKAGE: Package,
  DISCARD: Trash2,
  SUPPLIER_RETURN: Truck,
};

// Hook to get positions for a warehouse
function usePositionsByWarehouse(warehouseId: string | undefined) {
  return useQuery({
    queryKey: ["positions", "by-warehouse", warehouseId],
    queryFn: async () => {
      if (!warehouseId) return [];

      const { data, error } = await supabase
        .from("wms_positions")
        .select(
          `
          id,
          full_code,
          barcode,
          rack:wms_racks(
            hallway:wms_hallways(
              warehouse_id
            )
          )
        `
        )
        .eq("is_active", true);

      if (error) throw error;

      // Filter positions by warehouse
      return (data || [])
        .filter((p) => p.rack?.hallway?.warehouse_id === warehouseId)
        .map((p) => ({
          id: p.id,
          full_code: p.full_code,
          barcode: p.barcode,
        }));
    },
    enabled: !!warehouseId,
  });
}

export function ReturnPutawayPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id: userId } = useProfileStore();

  const [selectedInspection, setSelectedInspection] = useState<
    (ReturnInspection & { product_name?: string }) | null
  >(null);
  const [putawayDialogOpen, setPutawayDialogOpen] = useState(false);
  const [selectedPositionId, setSelectedPositionId] = useState<string>("");

  const { data: returnRequest, isLoading: requestLoading } =
    useReturnRequest(id);
  const { data: items = [], isLoading: itemsLoading } =
    useReturnRequestItems(id);
  const routeInspectionMutation = useRouteInspection();
  const updateStatusMutation = useUpdateReturnRequestStatus();
  const checkCompleteMutation = useCheckAndCompleteReturnRequest();

  // Get positions for selected inspection's warehouse
  const { data: positions = [] } = usePositionsByWarehouse(
    selectedInspection?.destination_warehouse_id || undefined
  );

  const isLoading = requestLoading || itemsLoading;

  // Collect all inspections that need routing
  const allInspections: (ReturnInspection & { product_name?: string })[] = [];
  items.forEach((item) => {
    if (item.inspections) {
      item.inspections.forEach((insp) => {
        allInspections.push({
          ...insp,
          product_name: item.product?.short_name || "Producto",
        });
      });
    }
  });

  const pendingInspections = allInspections.filter((insp) => !insp.routed_at);
  const routedInspections = allInspections.filter((insp) => insp.routed_at);
  const progress =
    allInspections.length > 0
      ? (routedInspections.length / allInspections.length) * 100
      : 0;

  const handlePutaway = (
    inspection: ReturnInspection & { product_name?: string }
  ) => {
    setSelectedInspection(inspection);
    setSelectedPositionId("");
    setPutawayDialogOpen(true);
  };

  const confirmPutaway = () => {
    if (!selectedInspection || !userId || !selectedPositionId) return;

    routeInspectionMutation.mutate(
      {
        formData: {
          inspection_id: selectedInspection.id,
          destination_position_id: selectedPositionId,
        },
        userId,
      },
      {
        onSuccess: () => {
          toast({
            title: "Ubicación registrada",
            description: `${selectedInspection.product_name} ubicado correctamente`,
          });
          setPutawayDialogOpen(false);
          setSelectedInspection(null);

          // Check if all items are routed
          const remainingPending = pendingInspections.filter(
            (i) => i.id !== selectedInspection.id
          ).length;

          if (remainingPending === 0 && id) {
            // All items routed, check and complete
            checkCompleteMutation.mutate(id, {
              onSuccess: (completed) => {
                if (completed) {
                  toast({
                    title: "Devolución completada",
                    description:
                      "Todos los items han sido ubicados correctamente.",
                  });
                  navigate(`/wms/returns/${id}`);
                }
              },
            });
          }
        },
        onError: (error) => {
          toast({
            title: "Error",
            description: `No se pudo registrar la ubicación: ${error.message}`,
            variant: "destructive",
          });
        },
      }
    );
  };

  const handleComplete = () => {
    if (!id) return;

    updateStatusMutation.mutate(
      { id, status: "completed" },
      {
        onSuccess: () => {
          toast({
            title: "Devolución completada",
            description: "La devolución ha sido procesada correctamente.",
          });
          navigate(`/wms/returns/${id}`);
        },
      }
    );
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </PageContainer>
    );
  }

  if (!returnRequest) {
    return (
      <PageContainer>
        <Card>
          <CardContent className="py-8 text-center">
            <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-yellow-500" />
            <p className="text-muted-foreground">Devolución no encontrada</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate("/wms/returns")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a Devoluciones
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Ubicación de Devolución"
        description={`${returnRequest.return_number} - ${returnRequest.client?.name || "Sin cliente"}`}
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/wms/returns")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
      />

      <div className="mx-auto w-full max-w-2xl space-y-6">
        {/* Progress Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  Progreso de ubicación
                </span>
                <span className="font-medium">
                  {routedInspections.length} / {allInspections.length} items
                </span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          </CardContent>
        </Card>

        {/* Pending Putaway */}
        {pendingInspections.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <MapPin className="h-4 w-4" />
                Items por Ubicar ({pendingInspections.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {pendingInspections.map((inspection) => {
                const Icon = dispositionIcons[inspection.disposition];
                return (
                  <div
                    key={inspection.id}
                    className="flex items-center justify-between rounded-lg border p-4"
                  >
                    <div className="space-y-2">
                      <p className="font-medium">{inspection.product_name}</p>
                      <div className="flex flex-wrap gap-2">
                        <Badge
                          className={DISPOSITION_COLORS[inspection.disposition]}
                          variant="outline"
                        >
                          <Icon className="mr-1 h-3 w-3" />
                          {DISPOSITION_LABELS[inspection.disposition]}
                        </Badge>
                        <Badge variant="secondary">
                          {inspection.quantity} unidades
                        </Badge>
                      </div>
                      {inspection.discard_reason && (
                        <p className="text-xs text-muted-foreground">
                          Razón:{" "}
                          {DISCARD_REASON_LABELS[
                            inspection.discard_reason as keyof typeof DISCARD_REASON_LABELS
                          ] || inspection.discard_reason}
                        </p>
                      )}
                      {inspection.destination_warehouse && (
                        <p className="text-xs text-muted-foreground">
                          Bodega: {inspection.destination_warehouse.code} -{" "}
                          {inspection.destination_warehouse.name}
                        </p>
                      )}
                    </div>
                    <Button onClick={() => handlePutaway(inspection)}>
                      <QrCode className="mr-2 h-4 w-4" />
                      Ubicar
                    </Button>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Routed Items */}
        {routedInspections.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Items Ubicados ({routedInspections.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {routedInspections.map((inspection) => {
                const Icon = dispositionIcons[inspection.disposition];
                return (
                  <div
                    key={inspection.id}
                    className="rounded-lg border border-green-200 bg-green-50 p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <p className="font-medium">{inspection.product_name}</p>
                        <div className="flex flex-wrap gap-2">
                          <Badge
                            className={
                              DISPOSITION_COLORS[inspection.disposition]
                            }
                            variant="outline"
                          >
                            <Icon className="mr-1 h-3 w-3" />
                            {DISPOSITION_LABELS[inspection.disposition]}
                          </Badge>
                          <Badge variant="secondary">
                            {inspection.quantity} unidades
                          </Badge>
                        </div>
                        {inspection.destination_position && (
                          <p className="text-xs text-green-700">
                            Ubicación:{" "}
                            {inspection.destination_position.full_code}
                          </p>
                        )}
                      </div>
                      <CheckCircle2 className="h-6 w-6 text-green-500" />
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Complete Button */}
        {routedInspections.length > 0 && pendingInspections.length === 0 && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="font-medium">Ubicación Completada</p>
                    <p className="text-sm text-muted-foreground">
                      Todos los items han sido ubicados
                    </p>
                  </div>
                </div>
                <Button onClick={handleComplete}>Finalizar Devolución</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Putaway Dialog */}
        <Dialog open={putawayDialogOpen} onOpenChange={setPutawayDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ubicar Producto</DialogTitle>
              <DialogDescription>
                Seleccione la posición de destino para este producto
              </DialogDescription>
            </DialogHeader>
            {selectedInspection && (
              <div className="space-y-4 py-4">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Producto
                  </Label>
                  <p className="font-medium">
                    {selectedInspection.product_name}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                      Disposición
                    </Label>
                    <Badge
                      className={
                        DISPOSITION_COLORS[selectedInspection.disposition]
                      }
                      variant="outline"
                    >
                      {DISPOSITION_LABELS[selectedInspection.disposition]}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                      Cantidad
                    </Label>
                    <p className="text-xl font-bold">
                      {selectedInspection.quantity}
                    </p>
                  </div>
                </div>

                {selectedInspection.destination_warehouse && (
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                      Bodega Destino
                    </Label>
                    <p className="text-sm">
                      {selectedInspection.destination_warehouse.code} -{" "}
                      {selectedInspection.destination_warehouse.name}
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Posición de Destino *</Label>
                  <Select
                    value={selectedPositionId}
                    onValueChange={setSelectedPositionId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccione una posición" />
                    </SelectTrigger>
                    <SelectContent>
                      {positions.map((pos) => (
                        <SelectItem key={pos.id} value={pos.id}>
                          {pos.full_code}
                          {pos.barcode && ` (${pos.barcode})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {positions.length === 0 &&
                    selectedInspection.destination_warehouse_id && (
                      <p className="text-xs text-muted-foreground">
                        No hay posiciones disponibles en esta bodega
                      </p>
                    )}
                  {!selectedInspection.destination_warehouse_id && (
                    <p className="text-xs text-yellow-600">
                      No se seleccionó una bodega destino durante la inspección
                    </p>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setPutawayDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmPutaway}
                disabled={
                  routeInspectionMutation.isPending || !selectedPositionId
                }
              >
                {routeInspectionMutation.isPending
                  ? "Guardando..."
                  : "Confirmar Ubicación"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PageContainer>
  );
}

export default ReturnPutawayPage;
